import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../../app/components/primitives/button";
import { cn } from "../../app/components/primitives/utils";
import { AttentionDot } from "./attention";

// The list-and-detail layout of the Items tab, the same on the quote and
// the order page and whatever their size (Ben, 5 Oct): a list of lines on
// the left, each with a status line and an attention dot, and the chosen
// line's detail on the right.
//   - On a phone: the list; tap a line for its detail, with ‹ Back.
//   - With one line there is nothing to choose between: its detail shows
//     full width, with no list and no line navigation.
// Which line is open lives in the URL (`?item=`), so a link can land on it.

export interface ListDetailItem {
  key: string;
  /** "01": the customer's line ref. */
  ref: string;
  title: string;
  status: string;
  hasAttention: boolean;
}

/** A line staff cancelled: named and dated, greyed, after the live lines, and
 *  nothing to open (no price, no reason: Ben, 7 Oct). */
export interface CancelledListItem {
  ref: string;
  title: string;
  /** "Cancelled on Wed 7 Oct", the read's words. */
  status: string;
}

function CancelledRows({ items }: { items: CancelledListItem[] }) {
  return items.map((item) => (
    <li key={item.ref} className="space-y-0.5 px-4 py-3 text-muted-foreground">
      <span className="flex items-center gap-2 text-sm">
        <span className="tabular-nums">{item.ref}</span>
        <span className="truncate">{item.title}</span>
      </span>
      <span className="block text-xs">{item.status}</span>
    </li>
  ));
}

export function ItemsListDetail({
  items,
  cancelled = [],
  selectedKey,
  defaultKey,
  onSelect,
  backLabel,
  renderDetail,
}: {
  items: ListDetailItem[];
  /** Cancelled lines, listed quietly after the live ones. */
  cancelled?: CancelledListItem[];
  /** The line the URL asks for; null when none. */
  selectedKey: string | null;
  /** What a wide screen shows beside the list when the URL names none. */
  defaultKey: string | null;
  onSelect: (key: string | null) => void;
  backLabel: string;
  renderDetail: (key: string) => React.ReactNode;
}) {
  // With one live line there is no list to hold the cancelled ones (and with
  // none, nothing to choose), so they list on their own below.
  const cancelledOnly =
    cancelled.length > 0 ? (
      <ul
        className="divide-y rounded-xl border bg-card"
        aria-label="Cancelled items"
      >
        <CancelledRows items={cancelled} />
      </ul>
    ) : null;

  if (items.length === 0 && cancelledOnly) return cancelledOnly;

  if (items.length === 1) {
    return (
      <>
        <div className="rounded-xl border bg-card p-4 sm:p-5">
          {renderDetail(items[0].key)}
        </div>
        {cancelledOnly}
      </>
    );
  }

  const shownKey = selectedKey ?? defaultKey;

  const list = (
    <ul className="divide-y" aria-label="Items">
      {items.map((item) => {
        const isCurrent = item.key === shownKey;
        return (
          <li key={item.key}>
            <button
              type="button"
              onClick={() => onSelect(item.key)}
              aria-current={isCurrent ? "true" : undefined}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40",
                isCurrent &&
                  "md:bg-muted/60 md:shadow-[inset_3px_0_0_var(--color-primary)]",
              )}
            >
              <span className="min-w-0 flex-1 space-y-0.5">
                <span className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {item.ref}
                  </span>
                  <span className="truncate text-sm font-medium">
                    {item.title}
                  </span>
                  {item.hasAttention && <AttentionDot />}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {item.status}
                </span>
              </span>
              <ChevronRight className="size-4 shrink-0 text-muted-foreground md:hidden" />
            </button>
          </li>
        );
      })}
      <CancelledRows items={cancelled} />
    </ul>
  );

  return (
    <div className="overflow-hidden rounded-xl border bg-card md:grid md:grid-cols-[minmax(13rem,18rem)_minmax(0,1fr)]">
      <div
        className={cn("md:border-r", selectedKey ? "hidden md:block" : "block")}
      >
        {list}
      </div>
      <div
        className={cn(
          "min-w-0 p-4 sm:p-5",
          selectedKey ? "block" : "hidden md:block",
        )}
      >
        {selectedKey && (
          <div className="mb-2 md:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2"
              onClick={() => onSelect(null)}
            >
              <ChevronLeft className="size-4" />
              {backLabel}
            </Button>
          </div>
        )}
        {shownKey && renderDetail(shownKey)}
      </div>
    </div>
  );
}
